import { Badge, Button, Card, Section } from '@memorizecards/ui';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { TituloPagina } from '../components/estado';
import { formatBRL, formatDate } from '../lib/format';
import { useConta } from '../lib/queries';
import type { PlanCode } from '../lib/types';
import { useAuth } from '../state/auth';

const SITE = 'https://memorizecards.com.br';

/**
 * Página de bloqueio.
 *
 * Aparece quando a assinatura venceu, foi cancelada ou a conta foi bloqueada.
 * O tom é o de quem quer o aluno de volta, não o de um portão fechado: o
 * progresso continua guardado e é dito explicitamente, porque o medo de perder
 * o histórico é o que faz mais gente desistir de renovar.
 */
export function Expirado(): ReactNode {
  const { user } = useAuth();
  const { data } = useConta();

  const bloqueada = user?.acesso.motivo === 'blocked';
  const planos = data?.planos;

  return (
    <Section spacing="md" width="reading">
      <TituloPagina
        eyebrow={bloqueada ? 'Conta bloqueada' : 'Assinatura vencida'}
        titulo={bloqueada ? 'Sua conta está bloqueada' : 'Seu acesso está pausado'}
        descricao={
          bloqueada
            ? 'Fale com o suporte pelo contato@memorizecards.com.br para reativar.'
            : 'Renove para voltar a estudar. Todo o seu progresso, anotações e favoritas continuam guardados.'
        }
      />

      {user?.expiraEm && !bloqueada ? (
        <Card padding="md" className="mb-8">
          <p className="text-sm text-mc-ink2">
            Seu último período terminou em{' '}
            <span className="text-mc-ink">{formatDate(user.expiraEm)}</span>.
          </p>
        </Card>
      ) : null}

      {!bloqueada && planos ? (
        <div className="grid gap-4 md:grid-cols-3">
          {(Object.values(planos) as Array<(typeof planos)[PlanCode]>).map((plano) => (
            <Card
              key={plano.code}
              padding="lg"
              active={plano.highlight === true}
              dimmed={plano.highlight !== true}
              className="flex flex-col gap-4"
            >
              <div className="flex items-start justify-between gap-2">
                <h2 className="text-mc-card-title font-medium text-mc-ink">
                  {plano.name}
                </h2>
                {plano.savingsLabel ? (
                  <Badge tone="accent">{plano.savingsLabel}</Badge>
                ) : null}
              </div>

              <div>
                <p className="font-mc-display text-3xl font-semibold text-mc-ink">
                  {formatBRL(plano.priceCents)}
                </p>
                <p className="mc-mono mt-1 normal-case tracking-normal">
                  {formatBRL(plano.monthlyEquivalentCents)} por mês
                </p>
              </div>

              <a
                href={`${SITE}/checkout?plano=${plano.code}&email=${encodeURIComponent(
                  user?.email ?? '',
                )}`}
                className="mt-auto"
              >
                <Button fullWidth>Renovar</Button>
              </a>
            </Card>
          ))}
        </div>
      ) : null}

      <div className="mt-10 flex flex-wrap justify-center gap-3">
        <Link to="/conta">
          <Button variant="ghost">Ver minha conta</Button>
        </Link>
        <a href={`${SITE}/contato`}>
          <Button variant="quiet">Falar com o suporte</Button>
        </a>
      </div>
    </Section>
  );
}
